// @givmo/board — "Open" on minutes waiting for the person's signature where the board's read names
// none, a read made while their signing window was shut (CBG-FIX74; frames b1–b3, c1, m1;
// `director-waiting.ts` says when the line shows).
//
// There is no "Confirm" button (Jonathan, D-CBG-18: "when you go to sign, if we need you to confirm,
// we should just automatically bring up the authentication"). Open brings the sheet up by itself, in
// its own row's words (`minutes_waiting`: confirming here signs nothing), then reads `/me` and the
// board's attention afresh — never a value from before the confirmation, the closed-minutes opening's
// rail — and goes on. A window already open (that read outlived it) brings no sheet: the same reads,
// then on:
//   * one waiting: straight to its minutes (frame d5), Sign ready;
//   * several: nowhere — the to-do list, told so (`afterwards`), opens with each as the RC10 row;
//   * none (signed or withdrawn meanwhile): nowhere, and the line is gone.
// A dismissed sheet leaves the page as it was. A confirmation that doesn't open the window, or a read
// that fails, says the closed-minutes opening's line. An account with neither a passkey nor an
// authenticator app meets Account Security's setup offer in the page's column instead of a sheet, as
// Sign does (`useDirectorAct`'s rule), and Open goes through the gate once a factor exists.
//
// During the pause after a sign-in method is removed (CBG-FIX127; `director/signing-pause.ts`) no
// confirmation can open the minutes: Open brings up no sheet and says when they open (S2), the setup
// offer still coming first for an account with neither factor.

import { useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OrgBoardDirectorAttentionOut } from '@givmo/api-client';
import { toast } from '@givmo/design-system';
import { useNavigate } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import { directorBoardSearch } from '../../director/director-board';
import { useBoardDirectorHost, type BoardDirectorSetupOutcome } from '../../director/director-host';
import { boardDirectorMeKey, decodeDirectorMe, useDirectorMe } from '../../director/director-me';
import { DID_NOT_OPEN } from '../../director/read-gate';
import { BOARD_SIGNING_ROWS } from '../../director/signing-copy';
import {
  BoardSigningWindowShut,
  needsFactorSetup,
  readHoldsAuthenticator,
  useBoardSigningGate,
  useLatestStepUpHost,
} from '../../director/signing-gate';
import { pauseEndsAt, signingPauseWords, useSigningPause } from '../../director/signing-pause';
import { DirectorSetupOfferPage } from './director-act';
import {
  approvalSignaturesAwaiting,
  type ApprovalSignatureAwaiting,
} from '../minutes/minutes-signatures-decode';

/**
 * What an Open ended on: the minutes (one waited, and it went to them), the list (several wait: each
 * is now its own RC10 row), or nothing (none any more, or no confirmation came).
 */
export type MinutesWaitingShown = 'minutes' | 'list' | 'nothing';

export interface MinutesWaitingOpen {
  /**
   * Open the line for this board (see the header); `afterwards` is told what it ended on (the to-do
   * list's own rule for what a choice from it brought, `director-todo.tsx`).
   */
  open: (orgRef: string, afterwards?: (shown: MinutesWaitingShown) => void) => void;
  /** The board whose Open is under way, or null. */
  opening: string | null;
  /** Open is under way and the person isn't at the sheet: its button spins only then. */
  working: boolean;
  /** The sheet, while a confirmation is outstanding: `null` where the host's provider draws it. */
  dialog: ReactNode;
  /** Account Security's setup offer, in the page's column, while it shows. */
  offer: ReactNode;
}

export function useMinutesWaitingOpen(identity?: ReactNode): MinutesWaitingOpen {
  const queryClient = useQueryClient();
  const operations = useBoardDirectorOperations();
  const navigate = useNavigate();
  const meQuery = useDirectorMe();
  const host = useBoardDirectorHost();
  const stepUpHost = useLatestStepUpHost();
  const gate = useBoardSigningGate(BOARD_SIGNING_ROWS.minutes_waiting);
  const [opening, setOpening] = useState<string | null>(null);
  const [offering, setOffering] = useState<string | null>(null);
  const pauseEnd = useSigningPause();
  // The caller's `afterwards`, kept across the setup offer, and told once.
  const afterwardsRef = useRef<((shown: MinutesWaitingShown) => void) | null>(null);
  const endedOn = (shown: MinutesWaitingShown) => {
    const then = afterwardsRef.current;
    afterwardsRef.current = null;
    then?.(shown);
  };

  /** CBG-FIX127 (S2): no sheet during the pause; `/me` is asked again for a later removal's end. */
  const holdForThePause = (endsAt: string) => {
    toast.error(signingPauseWords.reads(endsAt));
    void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
  };

  const runThrough = async (orgRef: string, accountHasPasskey: boolean | null | undefined) => {
    setOpening(orgRef);
    try {
      const outcome = await gate.run(async (): Promise<ApprovalSignatureAwaiting[]> => {
        // The server's answer about the window, read afresh with this moment's token.
        const me = await queryClient.fetchQuery({
          queryKey: boardDirectorMeKey(),
          staleTime: 0,
          queryFn: async () => {
            const { data } = await operations.directorGetBoardDirectorMe({ throwOnError: true });
            return decodeDirectorMe(data);
          },
        });
        if (!me.signing.open) throw new BoardSigningWindowShut();
        // Read again now the window is open: never a value from before the confirmation.
        const { data } = await operations.directorGetBoardDirectorAttention({
          path: { org_ref: orgRef },
          throwOnError: true,
        });
        queryClient.setQueryData<OrgBoardDirectorAttentionOut>(
          ['board-director-attention', orgRef],
          data,
        );
        return approvalSignaturesAwaiting(data.items);
      }, accountHasPasskey);
      switch (outcome.kind) {
        case 'done': {
          const [only, ...rest] = outcome.value;
          if (only !== undefined && rest.length === 0) {
            await navigate({
              to: BOARD_DIRECTOR_PAGES.record,
              params: { recordId: only.recordId },
              search: directorBoardSearch(orgRef),
            });
            endedOn('minutes');
            return;
          }
          endedOn(only === undefined ? 'nothing' : 'list');
          return;
        }
        case 'cancelled':
          endedOn('nothing');
          return;
        case 'needs-setup':
          // A passkey-only prompt found no passkey after all: the setup offer, not the sheet.
          if (host.setupOffer !== null) {
            setOffering(orgRef);
            return;
          }
          toast.error(DID_NOT_OPEN);
          endedOn('nothing');
          return;
        case 'refused-twice':
        case 'failed':
          toast.error(DID_NOT_OPEN);
          endedOn('nothing');
          return;
      }
    } finally {
      setOpening(null);
    }
  };

  const open = (orgRef: string, afterwards?: (shown: MinutesWaitingShown) => void) => {
    if (opening !== null || offering !== null) return;
    afterwardsRef.current = afterwards ?? null;
    const signing = meQuery.data?.signing;
    if (host.setupOffer === null || signing?.account_has_passkey !== false) {
      if (pauseEnd !== null) {
        holdForThePause(pauseEnd);
        endedOn('nothing');
        return;
      }
      void runThrough(orgRef, signing?.account_has_passkey);
      return;
    }
    // An account `/me` says holds no passkey may need the setup offer instead of a prompt: the
    // choice waits for the host's answer about the authenticator, with Open spinning meanwhile.
    void (async () => {
      setOpening(orgRef);
      const holdsAuthenticator = await readHoldsAuthenticator(stepUpHost);
      setOpening(null);
      if (needsFactorSetup(false, holdsAuthenticator)) {
        setOffering(orgRef);
        return;
      }
      if (pauseEnd !== null) {
        holdForThePause(pauseEnd);
        endedOn('nothing');
        return;
      }
      await runThrough(orgRef, false);
    })();
  };

  const settleOffer = (outcome: BoardDirectorSetupOutcome) => {
    const orgRef = offering;
    setOffering(null);
    if (outcome !== 'created' || orgRef === null) {
      endedOn('nothing');
      return;
    }
    // Setting a factor up opens no window: Open goes through the gate, and the confirmation comes
    // with the factor just made, once `/me` says which one now exists.
    void (async () => {
      setOpening(orgRef);
      let accountHasPasskey: boolean | null | undefined;
      let freshEnd: string | null = null;
      try {
        const fresh = await meQuery.refetch();
        if (fresh.isError) throw fresh.error;
        accountHasPasskey = fresh.data?.signing.account_has_passkey;
        freshEnd = pauseEndsAt(fresh.data?.signing, Date.now());
      } catch {
        setOpening(null);
        toast.error(DID_NOT_OPEN);
        endedOn('nothing');
        return;
      }
      if (freshEnd !== null) {
        // A factor made during the pause doesn't end it.
        setOpening(null);
        holdForThePause(freshEnd);
        endedOn('nothing');
        return;
      }
      await runThrough(orgRef, accountHasPasskey);
    })();
  };

  return {
    open,
    opening,
    working: opening !== null && !gate.prompting,
    dialog: gate.dialog,
    offer:
      offering === null ? null : (
        <DirectorSetupOfferPage variant="from-sign" identity={identity} onSettled={settleOffer} />
      ),
  };
}
